import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./NumberKey.css";

const SYMBOLS = ["🍎", "⭐", "🐶", "🚗", "🌸", "⚽", "🎈", "🦋"];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 10,
    keySize: 4,
  },
  medium: {
    label: "Media",
    totalRounds: 14,
    keySize: 6,
  },
  hard: {
    label: "Difícil",
    totalRounds: 18,
    keySize: 8,
  },
};

function shuffle(list) {
  return [...list].sort(() => Math.random() - 0.5);
}

function createKey(config) {
  return shuffle(SYMBOLS)
    .slice(0, config.keySize)
    .map((symbol, index) => ({
      symbol,
      number: index + 1,
    }));
}

function createRound(keyItems) {
  return keyItems[Math.floor(Math.random() * keyItems.length)];
}

export default function NumberKey() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);
  const [keyItems, setKeyItems] = useState([]);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const nextKey = createKey(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setKeyItems(nextKey);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStarted(true);
    setFinished(false);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound(nextKey));
  };

  const answer = (number) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = number === round.number;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: number,
        correctAnswer: round.number,
        symbol: round.symbol,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) setCorrect((prev) => prev + 1);
    else setErrors((prev) => prev + 1);

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound(keyItems));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setKeyItems([]);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Clave de números"
        description="Observa la clave y pulsa el número que corresponde a la imagen central."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Memoria visual",
          "Velocidad de procesamiento",
        ]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="number-key-page">
      <div className="number-key-card">
        <header className="number-key-header">
          <div>
            <h1>Clave de números</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="number-key-reference">
              {keyItems.map((item) => (
                <div key={item.symbol} className="key-item">
                  <span className="key-symbol">{item.symbol}</span>
                  <strong>{item.number}</strong>
                </div>
              ))}
            </section>

            <section className="number-key-main">
              <span className="target-symbol">{round.symbol}</span>
              <p>¿Qué número le corresponde?</p>
            </section>

            <section
              className={`number-key-options options-${config.keySize}`}
            >
              {keyItems.map((item) => (
                <button
                  key={item.number}
                  type="button"
                  onClick={() => answer(item.number)}
                >
                  {item.number}
                </button>
              ))}
            </section>

            <section className="number-key-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}